import React, { useState, useEffect } from 'react';
import {
    FaCog,
    FaShieldAlt,
    FaUniversity,
    FaSearch,
    FaToggleOn,
    FaToggleOff,
    FaMoneyBillWave,
    FaSave,
    FaCheckCircle,
    FaExclamationCircle
} from 'react-icons/fa';
import * as adminService from '../services/adminService';
import * as masterService from '../services/masterService';
import Toast from '../components/Toast';

const AdminSettings = () => {
    const [settings, setSettings] = useState({
        signup_payment_enabled: 'false',
        signup_price: '0'
    });
    const [universities, setUniversities] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [savingSetting, setSavingSetting] = useState(null);
    const [toast, setToast] = useState(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [settingsRes, uniRes] = await Promise.all([
                adminService.getGlobalSettings(),
                masterService.getAllUniversities()
            ]);

            if (settingsRes.success) {
                setSettings(prev => ({ ...prev, ...settingsRes.data }));
            }

            if (uniRes.success) {
                setUniversities(uniRes.data || []);
            }
        } catch (error) {
            showToast(error.message || 'Failed to load settings', 'error');
        } finally {
            setLoading(false);
        }
    };

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
    };

    const handleSettingUpdate = async (key, value) => {
        setSavingSetting(key);
        try {
            const response = await adminService.updateGlobalSetting(key, value);
            if (response.success) {
                setSettings(prev => ({ ...prev, [key]: String(value) }));
                showToast(`Setting ${key} updated successfully`);

                // Requirement: Refetch universities to show the updated status whenever global payment is toggled
                if (key === 'signup_payment_enabled') {
                    const uniRes = await masterService.getAllUniversities();
                    if (uniRes.success) {
                        setUniversities(uniRes.data || []);
                    }
                }
            }
        } catch (error) {
            showToast(error.message || 'Update failed', 'error');
        } finally {
            setSavingSetting(null);
        }
    };

    const handleUniToggle = async (uniId, currentStatus) => {
        try {
            const response = await adminService.toggleUniversityFreeSignup(uniId, !currentStatus);
            if (response.success) {
                setUniversities(prev => prev.map(u =>
                    u.id === uniId ? { ...u, is_free_signup: !currentStatus } : u
                ));
                showToast('University status updated');
            }
        } catch (error) {
            showToast(error.message || 'Toggle failed', 'error');
        }
    };

    const filteredUnis = universities.filter(u =>
        u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.state?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const darkGradient = {
        background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 pb-12">
            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

            {/* Header */}
            <div className="py-12 px-4 shadow-lg text-white" style={darkGradient}>
                <div className="max-w-7xl mx-auto flex items-center gap-4">
                    <div className="p-3 bg-white/10 rounded-2xl backdrop-blur-md">
                        <FaCog className="text-3xl text-blue-400" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-extrabold">System Settings</h1>
                        <p className="text-blue-100 opacity-80">Manage signup payments and university exemptions</p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column: Global Settings */}
                <div className="lg:col-span-1 space-y-6">
                    <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
                        <div className="p-6 bg-gradient-to-r from-indigo-500 to-blue-600 text-white flex items-center gap-3">
                            <FaShieldAlt />
                            <h2 className="font-bold">Signup Payment Control</h2>
                        </div>
                        <div className="p-8 space-y-8">
                            {/* Toggle */}
                            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100">
                                <div>
                                    <p className="font-bold text-gray-900">Enable Payment</p>
                                    <p className="text-xs text-gray-500">Require fee during registration</p>
                                </div>
                                <button
                                    onClick={() => handleSettingUpdate('signup_payment_enabled', settings.signup_payment_enabled === 'true' ? 'false' : 'true')}
                                    className={`text-4xl transition-colors ${settings.signup_payment_enabled === 'true' ? 'text-green-500' : 'text-gray-300'}`}
                                    disabled={savingSetting === 'signup_payment_enabled'}
                                >
                                    {settings.signup_payment_enabled === 'true' ? <FaToggleOn /> : <FaToggleOff />}
                                </button>
                            </div>

                            {/* Price Input */}
                            <div className="space-y-3">
                                <label className="block font-bold text-gray-700 flex items-center gap-2">
                                    <FaMoneyBillWave className="text-green-500" />
                                    Signup Fee (INR)
                                </label>
                                <div className="flex gap-2">
                                    <div className="relative flex-grow">
                                        <span className="absolute left-4 top-1/2 -translate-y-1/2 font-bold text-gray-400">₹</span>
                                        <input
                                            type="number"
                                            value={settings.signup_price}
                                            onChange={(e) => setSettings(prev => ({ ...prev, signup_price: e.target.value }))}
                                            className="w-full pl-8 pr-4 py-3 bg-gray-50 border-2 border-gray-100 rounded-xl focus:border-indigo-500 outline-none font-bold"
                                        />
                                    </div>
                                    <button
                                        onClick={() => handleSettingUpdate('signup_price', settings.signup_price)}
                                        disabled={savingSetting === 'signup_price'}
                                        className="px-6 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all flex items-center gap-2 shadow-lg shadow-indigo-200"
                                    >
                                        <FaSave />
                                        Save
                                    </button>
                                </div>
                            </div>

                            <div className="p-4 bg-blue-50 rounded-2xl border border-blue-100 flex gap-3">
                                <FaExclamationCircle className="text-blue-500 mt-1 flex-shrink-0" />
                                <p className="text-xs text-blue-700 leading-relaxed">
                                    When enabled, new users must pay this amount to access most platform features.
                                    Existing users and universities with "Payment Required" turned OFF are exempted.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column: University Exemptions */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100 flex flex-col h-[700px]">
                        <div className="p-6 bg-gradient-to-r from-emerald-500 to-teal-600 text-white flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <FaUniversity />
                                <h2 className="font-bold">University Exemption List</h2>
                            </div>
                            <span className="bg-white/20 px-3 py-1 rounded-full text-xs font-bold">
                                {universities.length} Universities
                            </span>
                        </div>

                        {/* Search Bar */}
                        <div className="p-6 border-b border-gray-100">
                            <div className="relative">
                                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search university by name or state..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-12 pr-4 py-3 bg-gray-50 border-2 border-gray-100 rounded-2xl focus:border-emerald-500 outline-none transition-all"
                                />
                            </div>
                        </div>

                        {/* Uni List */}
                        <div className="flex-grow overflow-y-auto p-0 scrollbar-thin">
                            <table className="w-full text-left">
                                <thead className="bg-gray-50 sticky top-0 z-10">
                                    <tr>
                                        <th className="px-8 py-4 text-xs font-bold text-gray-500 uppercase">University Name</th>
                                        <th className="px-8 py-4 text-xs font-bold text-gray-500 uppercase">State</th>
                                        <th className="px-8 py-4 text-xs font-bold text-gray-500 uppercase text-center">Payment Required</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {filteredUnis.length === 0 ? (
                                        <tr>
                                            <td colSpan="3" className="px-8 py-12 text-center text-gray-400 font-medium italic">
                                                No universities found matching "{searchTerm}"
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredUnis.map(uni => (
                                            <tr key={uni.id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-8 py-5">
                                                    <p className="font-bold text-gray-900">{uni.name}</p>
                                                    <p className="text-[10px] text-gray-400 font-mono tracking-tighter">ID: {uni.id}</p>
                                                </td>
                                                <td className="px-8 py-5">
                                                    <span className="text-sm text-gray-600 font-medium">{uni.state || 'N/A'}</span>
                                                </td>
                                                <td className="px-8 py-5 text-center">
                                                    <button
                                                        onClick={() => handleUniToggle(uni.id, uni.is_free_signup)}
                                                        className={`text-4xl transition-all hover:scale-110 active:scale-95 ${!uni.is_free_signup ? 'text-indigo-600' : 'text-gray-300'}`}
                                                    >
                                                        {!uni.is_free_signup ? <FaToggleOn /> : <FaToggleOff />}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminSettings;
